import React, { FC, useCallback, useReducer, useState } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { action } from 'typesafe-actions';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { authorizationService, payrollService } from '../../../../../channels/secure-web/services';
import { useSelectedCustomerId } from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { MandatePayroll } from '../../domain/Authorization/entities';
import { CheckMandateResponseBody } from '../../domain/Authorization/entities/CheckMandateResponseDTO';
import { AuthorizationPayrollContext, AuthorizationPayrollDispatchContext } from './AuthorizationPayrollContext';
import { defaultAuthorizationPayrollState } from './defaultState';
import { AuthorizationPayrollReducer } from './AuthorizationPayrollReducer';
import { AuthorizationPayrollContextActionTypes } from './types';

export interface PayrollProviderProps {
  payrollList: Nullable<MandatePayroll[]>;
}

const AuthorizationPayrollProvider: FC<PayrollProviderProps> = ({ children, payrollList }) => {
  // Get initial state values
  const [state, dispatch] = useReducer(AuthorizationPayrollReducer, defaultAuthorizationPayrollState);
  const [timestamp] = useState(new Date().toISOString());
  const customerId = useSelectedCustomerId();


  // Payroll file details api call definition
  const shouldFetchPayrollDetails = !isEmpty(payrollList) && !isEmpty(state.selectedPayroll);
  const {
    data: payrollDetails,
    error: payrollDetailsError,
    isLoading: isLoadingPayrollDetails,
    mutate: refetchPayrollDetails,
  } = useInvokeSWRService(
    payrollService.GetPayrollDetails,
    {
      customerId,
      fileId: state.selectedPayroll?.payrollFileId,
      meta: {
        timestamp,
      },
    },
    shouldFetchPayrollDetails,
  );

  const shouldCheckAuthorization = Boolean(state.selectedPayroll) && state.selectedPayroll !== null;

  // Check Authorization
  const {
    data: checkAuthorizationData,
    isLoading: isLoadingCheckAuthorization,
    error: checkAuthorizationError,
    mutate: checkAuthorizationMutation,
  } = useInvokeSWRService<CheckMandateResponseBody>(
    authorizationService.CheckMandate,
    {
      accountNumber: state.selectedPayroll?.fromAccount,
      uniqueId: Number(state.selectedPayroll?.payrollFileId),
      amount: Number(state.selectedPayroll?.amount),
      meta: {
        timestamp: state.selectedPayroll?.timestamp,
      },
    },
    shouldCheckAuthorization,
  );

  const onSelectPayroll = useCallback(
    (payrollFileId?: string) => {
      if (payrollFileId && payrollList) {
        const payroll = (payrollList as MandatePayroll[]).find(payroll => payroll.payrollFileId === payrollFileId);
        dispatch(
          action(AuthorizationPayrollContextActionTypes.SetSelectedPayroll, {
            ...payroll,
            timestamp: state?.selectedPayroll?.timestamp || Date.now().toString(),
          }),
        );
      }
    },
    [payrollList, state?.selectedPayroll?.timestamp],
  );

  const onResetSelectedPayroll = useCallback(() => {
    dispatch(action(AuthorizationPayrollContextActionTypes.SetSelectedPayroll, null));
  }, []);

  const initiateMandateService = useCallback(() => authorizationService.InitiateMandate, []);

  const executeMandateService = useCallback(() => authorizationService.ExecuteMandate, []);

  const onRefetchPayrollDetailsMutation = useCallback(() => {
    refetchPayrollDetails && refetchPayrollDetails();
  }, [refetchPayrollDetails]);

  const onCheckAuthorizationMutation = useCallback(() => {
    checkAuthorizationMutation && checkAuthorizationMutation();
  }, [checkAuthorizationMutation]);

  const onToggleAuthPayrollFlowDrawer = useCallback(() => {
    dispatch(
      action(AuthorizationPayrollContextActionTypes.SetAuthPayrollFlowDrawerOpen, !state.isAuthPayrollFlowDrawerOpen),
    );
  }, [state.isAuthPayrollFlowDrawerOpen]);

  return (
    <AuthorizationPayrollContext.Provider
      value={{
        ...state,
        payrollList,
        payrollDetails,
        checkAuthorizationData,
        onSelectPayroll,
        onResetSelectedPayroll,
        loadingState: {
          isLoadingPayrollDetails,
          isLoadingCheckAuthorization,
        },
        errorState: {
          payrollDetailsError,
          checkAuthorizationError,
        },
        initiateMandateService,
        onRefetchPayrollDetailsMutation,
        onCheckAuthorizationMutation,
        executeMandateService,
        onToggleAuthPayrollFlowDrawer,
      }}>
      <AuthorizationPayrollDispatchContext.Provider value={dispatch}>
        {children}
      </AuthorizationPayrollDispatchContext.Provider>
    </AuthorizationPayrollContext.Provider>
  );
};

export { AuthorizationPayrollProvider };
